import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { useTeamspace } from './TeamspaceProvider';

// Only the Invoices badge remains. The Invoice Items ("unbilled") dot was dropped —
// unbilled items are the normal resting state of that tab, so a dot that is almost
// always lit carries no information. The summary endpoint still reports the unbilled
// count; nothing consumes it here.
// Trailing window for collapsing a burst of refreshBadges() calls into one request.
// Short enough that the badge still feels immediate after a user action.
const BADGE_DEBOUNCE_MS = 300;

const InvoicingBadgeContext = createContext({
  hasActiveInvoices: false,
  refreshBadges: () => {},
});

export function InvoicingBadgeProvider({ children }) {
  const { selectedTeamspaceId } = useTeamspace();
  const [hasActiveInvoices, setHasActiveInvoices] = useState(false);
  // Kept in a ref so refreshBadges stays referentially stable — it goes into
  // InvoicingPage effect deps, and a new identity per teamspace change would
  // re-trigger them.
  const teamspaceRef = useRef(selectedTeamspaceId);
  teamspaceRef.current = selectedTeamspaceId;

  // This runs on every page of the app, not just the invoicing one, so it has to
  // stay cheap: the summary endpoint answers with COUNT(*)s. It used to pull the
  // full invoice_items and invoices tables and intersect them in the browser.
  const fetchBadges = useCallback(async (teamspaceId) => {
    if (!teamspaceId) return null;
    const res = await axios.get(`${API_BASE_URL}teamspaces/${teamspaceId}/invoice_items/summary/`);
    return res.data;
  }, []);

  useEffect(() => {
    if (!selectedTeamspaceId) return undefined;
    let cancelled = false;
    fetchBadges(selectedTeamspaceId)
      .then(data => {
        if (cancelled || !data) return;
        setHasActiveInvoices(!!data.has_active_invoices);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedTeamspaceId, fetchBadges]);

  // Called by InvoicingPage after any mutation that could flip a badge (creating
  // an invoice, cancelling items, …). The page no longer holds every row, so it
  // can't recompute these itself.
  //
  // Coalesced on a short trailing debounce. Both fetchItems and fetchInvoices call
  // this, so a single ['invoices','items'] live-refresh fired two identical
  // COUNT(*) requests — multiplied by every tab open on the teamspace. A badge is
  // a count with latest-wins semantics, so collapsing a burst loses nothing.
  const badgeTimerRef = useRef(null);
  const refreshBadges = useCallback(() => {
    if (badgeTimerRef.current) clearTimeout(badgeTimerRef.current);
    badgeTimerRef.current = setTimeout(() => {
      badgeTimerRef.current = null;
      fetchBadges(teamspaceRef.current)
        .then(data => {
          if (!data) return;
          setHasActiveInvoices(!!data.has_active_invoices);
        })
        .catch(() => {});
    }, BADGE_DEBOUNCE_MS);
  }, [fetchBadges]);

  useEffect(() => () => {
    if (badgeTimerRef.current) clearTimeout(badgeTimerRef.current);
  }, []);

  return (
    <InvoicingBadgeContext.Provider value={{ hasActiveInvoices, refreshBadges }}>
      {children}
    </InvoicingBadgeContext.Provider>
  );
}

export const useInvoicingBadge = () => useContext(InvoicingBadgeContext);
